import {
  H2,
  Text,
  boxShadow,
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { PageContainer } from '../../../components/PageContainer/PageContainer';
import { DynamicContent } from '../DynamicContent/DynamicContent';
import type { DynamicContents, DynamicContentsItem } from '../DynamicContent/types';

const contentBrickWrapperAttributes = ({ $isHidden }: { $isHidden?: boolean }) => {
  return $isHidden ? { 'aria-hidden': true } : {};
};

export const ContentBrickWrapper = styled(PageContainer).attrs<{
  $isHidden?: boolean;
  $isSubBrick?: boolean;
}>(contentBrickWrapperAttributes)`
  ${boxShadow.defaultBoxShadow};
  background: ${paletteHelper('backgroundPrimaryHigh')};
  padding: ${spacingHelper('medium')};
  border-radius: ${({ $isSubBrick }) =>
    $isSubBrick ? radiusHelper('medium') : radiusHelper('large')};
  position: relative;

  ${mediaHelper('tabletMin')} {
    padding: ${spacingHelper('large')};
  }

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      display: none;
    `}
`;

const BrickHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
`;

const TitlesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('twoExtraSmall')};

  margin-bottom: ${spacingHelper('medium')};
`;

const Subtitle = styled(Text)`
  ${typographyHelper('subtitle02')};
  color: ${paletteHelper('textSecondary')};
`;

interface ContentBrickProps {
  id?: string;
  title?: string;
  titleComponent?: React.ReactNode;
  subtitle?: string | null;
  subtitleComponent?: React.ReactNode;
  subtitleAs?: 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  headerChildren?: React.ReactNode | null;
  subtitleWithDynamicContent?: DynamicContents<DynamicContentsItem> | null;
  isHidden?: boolean;
  isSubBrick?: boolean;
  className?: string;
}

const SubtitleWithDynamicContent = styled(Text)`
  ${typographyHelper('subtitle02')};
  > * {
    ${typographyHelper('subtitle02')};
  }
`;
export const ContentBrick: FunctionComponent<PropsWithChildren<ContentBrickProps>> = ({
  id,
  title,
  titleComponent,
  subtitle,
  subtitleAs = 'p',
  subtitleComponent,
  headerChildren,
  children,
  subtitleWithDynamicContent,
  isHidden = false,
  isSubBrick = false,
  className,
}) => {
  return (
    <ContentBrickWrapper
      as="section"
      aria-label={title}
      $isHidden={isHidden}
      $isSubBrick={isSubBrick}
      className={className}
      id={id}
    >
      {(titleComponent ||
        title ||
        subtitleComponent ||
        subtitle ||
        subtitleWithDynamicContent ||
        headerChildren) && (
        <BrickHeader className={className ? `${className}-header` : undefined}>
          <TitlesWrapper className={className ? `${className}-titles-wrapper` : undefined}>
            {titleComponent || (title && <H2 variant="subtitle01">{title}</H2>)}
            {subtitleComponent || (subtitle && <Subtitle as={subtitleAs}>{subtitle}</Subtitle>)}
            {subtitleWithDynamicContent && (
              <SubtitleWithDynamicContent as={subtitleAs}>
                <DynamicContent
                  dynamicContents={subtitleWithDynamicContent}
                  textColorVariant="textSecondary"
                />
              </SubtitleWithDynamicContent>
            )}
          </TitlesWrapper>
          {headerChildren}
        </BrickHeader>
      )}
      {children}
    </ContentBrickWrapper>
  );
};
